import React, {createContext, useContext, ReactNode, useEffect, useState, useMemo, useCallback, useRef} from "react";
import dayjs from "dayjs";

import "dayjs/locale/de"; // TODO: code splitting in future
import "dayjs/locale/en"; // TODO: code splitting in future
import "dayjs/locale/fr"; // TODO: code splitting in future
import "dayjs/locale/it"; // TODO: code splitting in future
import "dayjs/locale/es"; // TODO: code splitting in future
import "dayjs/locale/pt"; // TODO: code splitting in future
import {Language} from 'ts-core';

interface TranslationInfo {
    translated: (key: string, defaultValue?: string) => string;
    translate: (key: string, language: string) => string;
    language: Language;
    setLanguage: (language: Language) => void;
}

const TranslationsContext = createContext<TranslationInfo | undefined>(
    undefined
);

type TranslationProviderProps = {
    children: ReactNode;
    translations: any;
    language: Language,
};

const LANGUAGE_STORAGE_KEY = 'balloon_language';

const getInitialLanguage = (defaultLanguage: Language): Language => {
    if (typeof window === 'undefined') return defaultLanguage;
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && ['de', 'en', 'fr', 'it', 'es', 'pt'].includes(stored)) {
        return stored as Language;
    }
    return defaultLanguage;
};

export const TranslationsProvider: React.FC<TranslationProviderProps> = ({
    children,
    translations,
    language
}) => {

    const [currentLanguage, setCurrentLanguage] = useState<Language>(() => getInitialLanguage(language));
    const keys = translations[currentLanguage as keyof typeof translations];
    const translated = useCallback((key: string, defaultValue?: string) => {
        const translationKeys = translations[currentLanguage as keyof typeof translations];
        const value = translationKeys?.[key as keyof typeof translationKeys];
        if (value) return value;
        // Fall back to English before returning the raw key — under-translated
        // languages (e.g. PT, which currently has ~38 of ~540 keys) otherwise
        // surface `snake_case` identifiers directly to users.
        const englishKeys = translations.en;
        const englishValue = englishKeys?.[key as keyof typeof englishKeys];
        if (englishValue) {
            if (currentLanguage !== 'en') {
                console.warn(`Translation key "${key}" missing in ${currentLanguage}; falling back to EN`);
            }
            return englishValue;
        }
        console.warn(`Translation key "${key}" not found in any language`);
        return defaultValue || key;
    }, [currentLanguage, translations])

    const translate = useCallback((key: string, language: keyof typeof translations) => {
        try {
            const keys = translations[language];
            const value = keys?.[key as keyof typeof keys];
            if (value) return value;
            // Same EN fallback as `translated()`.
            const englishValue = translations.en?.[key as keyof typeof translations.en];
            return englishValue || key;
        } catch (e) {
            return key;
        }
    }, [translations])

    const handleSetLanguage = useCallback((newLanguage: Language) => {
        setCurrentLanguage(newLanguage);
        if (typeof window !== 'undefined') {
            localStorage.setItem(LANGUAGE_STORAGE_KEY, newLanguage);
        }
    }, []);

    // Sync language prop changes to internal state (but NOT on initial mount)
    // This is important for nested TranslationsProviders (e.g., map component)
    // that receive language as a prop from the parent.
    // We skip the first render to preserve localStorage-based initialization.
    const prevLanguageRef = useRef<Language | null>(null);
    useEffect(() => {
        // On first render, just record the initial prop value
        if (prevLanguageRef.current === null) {
            prevLanguageRef.current = language;
            return;
        }
        // Only sync if the prop actually changed
        if (prevLanguageRef.current !== language) {
            prevLanguageRef.current = language;
            setCurrentLanguage(language);
        }
    }, [language]);

    useEffect(() => {
        dayjs.locale(currentLanguage);
    }, [currentLanguage]);

    return (
        <TranslationsContext.Provider
            value={{
                translated,
                translate,
                language: currentLanguage,
                setLanguage: handleSetLanguage,
            }}
        >
            {children}
        </TranslationsContext.Provider>
    );
};

export function useTranslations(): TranslationInfo {
    const context = useContext(TranslationsContext);
    if (!context) {
        throw new Error(
            "useTranslations must be used within a TranslationsProvider"
        );
    }
    return context;
}
